/**
 * Intro Video screen — plays the admin-uploaded intro then routes to /player/menu.
 * Skippable; auto-skipped if no video configured.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPlayerBranding, getAssetUrl } from "../lib/playerApi";
import { PLAYER } from "../lib/testIds";
import { SkipForward } from "lucide-react";
import ScreenSizeSetup from "../components/ScreenSizeSetup";
import "../vu-theme.css";

export default function Intro() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loaded, setLoaded] = useState(false);
  // v1.4.45 — First-run screen-size prompt. Activation.jsx sets this
  // flag right before navigating here so the user can dial in the
  // right poster/UI size before landing on the main menu. Persists
  // until they hit "Continue" (or manually clear localStorage).
  const [showScreenSizeSetup, setShowScreenSizeSetup] = useState(() => {
    try { return localStorage.getItem("tv_player.firstRunScreenSize") === "1"; }
    catch { return false; }
  });

  useEffect(() => {
    // v1.4.45 — Don't auto-navigate to the menu while the first-run
    // screen-size dialog is showing; user must dismiss it first.
    if (showScreenSizeSetup) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const b = await getPlayerBranding();
        if (cancelled) return;
        const url = getAssetUrl(b.intro_video_url);
        if (url) {
          setVideoUrl(url);
          setLoaded(true);
        } else {
          navigate("/player/menu", { replace: true });
        }
      } catch {
        navigate("/player/menu", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, showScreenSizeSetup]);

  const goNext = () => navigate("/player/menu", { replace: true });

  if (showScreenSizeSetup) {
    return (
      <ScreenSizeSetup
        firstRun
        onDone={() => setShowScreenSizeSetup(false)}
      />
    );
  }

  if (!loaded) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-slate-400">
        Loading…
      </div>
    );
  }

  return (
    <div
      data-testid={PLAYER.introScreen}
      className="min-h-screen bg-black flex items-center justify-center relative"
    >
      <video
        ref={videoRef}
        src={videoUrl}
        autoPlay
        playsInline
        onEnded={goNext}
        onError={goNext}
        className="max-w-full max-h-screen"
      />
      <button
        data-testid={PLAYER.btnSkipIntro}
        onClick={goNext}
        className="absolute bottom-8 right-8 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white px-4 py-2 rounded-full flex items-center gap-2 text-sm font-medium transition-colors"
      >
        Skip
        <SkipForward className="w-4 h-4" />
      </button>
    </div>
  );
}
